import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UserService } from 'src/app/services/user.service';
import {BehaviorSubject} from "rxjs";

export interface TermsAndConditionsRow {
  id: number;
  version: number;
  endpoint: string | null;
  content?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Rolling Cognito login failure row (elevated admin API, 7-day window). */
export interface LoginAuthFailureRow {
  username: string;
  last_failed_at: string;
  failure_count: number;
  failure_category: string;
  user_message: string;
  admin_message?: string | null;
  cognito_code?: string | null;
  cognito_message?: string | null;
  flow: string;
  ip_address?: string | null;
  user_agent?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

/** Rolling kiln claim failure row (elevated admin API, 7-day window). */
export interface KilnClaimFailureRow {
  lookup_key: string;
  user_email: string;
  serial_number: string;
  last_failed_at: string;
  failure_count: number;
  failure_category: string;
  user_message: string;
  admin_message?: string | null;
  flow: string;
  account_id?: number | null;
  existing_account_id?: number | null;
  ip_address?: string | null;
  user_agent?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

/** Distributor row from claim-api (admin + account list). */
export interface DistributorApiRow {
  id: number;
  name: string;
  address: {
    line_one: string;
    line_two?: string | null;
    city: string;
    state: string;
    postal: string;
    country?: string;
  };
  users?: { id: number; email?: string; first_name?: string; last_name?: string; username?: string; type?: string }[];
}

interface AdminStorageData {
  success: boolean;
  message?: string;
  error?: string;
  data: {
    summary: {
      totalKilns: number;
      totalSerialNumbers: number;
      totalFiringCount: number;
      totalStorageSize: number;
      averageFiringSize: number;
      lastUpdated: string;
    };
    topSerialNumbers: {
      serialNumber: string;
      accountName: string;
      totalSize: number;
      firingCount: number;
    }[];
  }
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  public current_account_id;
  public elasticbeanstalk_uri = environment.claimApi;
  public local_uri = environment.claimApi;
  public accountInformation;
  public loginAsStatus = new BehaviorSubject<boolean>(false);

  constructor(
    public http: HttpClient,
    public userService: UserService) {
      this.local_uri = this.elasticbeanstalk_uri;
      this.current_account_id = undefined;
  }

  public getCurrentAccount() {
    if (this.current_account_id) {
      return this.current_account_id;
    }
    const loginData = this.userService.getLoginData();
    if (!loginData) {
      return undefined;
    }
    if (this.userService.isLoggedInAsAnotherUser() && loginData.loggedInAs?.account != null) {
      return loginData.loggedInAs.account;
    }
    return loginData.account;
  }

  public getAccountInformation(): Promise<any>{
    var accountUrl = `${this.local_uri}/account/${this.getCurrentAccount()}`;
    let getAccountPromise = this.http.get(accountUrl, this.httpOptions())
      .toPromise();

    getAccountPromise
      .then(response => {
        this.accountInformation = response;
        if(this.accountInformation && this.accountInformation.general) {
          // console.log(`switch current account from ${this.current_account_id} to ${this.accountInformation.general.external_id}`)
          this.current_account_id = this.accountInformation.general.external_id;
        }
      });

    return getAccountPromise;
  }

  public getAllAccounts(): Promise<any>{
    let is_logged_in_as = this.userService.isLoggedInAsAnotherUser();
    let loginInfo = this.userService.getLoginData().loggedInAs;
    var accountUrl = `${this.local_uri}/accounts${(is_logged_in_as && loginInfo && (loginInfo.email || loginInfo.username)) ? `?loginAs=${loginInfo.email || loginInfo.username}` : ''}`;
    let getAllAccountsPromise = this.http.get(accountUrl, this.httpOptions())
      .toPromise()

    getAllAccountsPromise
    .then(response => {
      this.accountInformation = response;
    });

    return getAllAccountsPromise;
  }

  public setDefault(account_id): Promise<any>{
    var accountUrl = `${this.local_uri}/accounts`;
    let setDefaultPromise = this.http.post(accountUrl, {default: account_id}, this.httpOptions())
      .toPromise()

    setDefaultPromise
      .then(response => {
        this.accountInformation = response;
        this.view(account_id);
      })

    return setDefaultPromise;
  }

  public view(account_id): Promise<any> {
    this.setCurrent(account_id);
    return this.userService.setCurrentAccount(account_id);
  }

  public unsetLoginAs() {
    this.current_account_id = undefined;
    this.loginAsStatus.next(false);
  }

  public setCurrent(account_id) {
    // console.log(`switch current account from ${this.current_account_id} to ${account_id}`)
    this.current_account_id = account_id;
  }

  public listAccountInformation(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/account/list`;
    let accountPromise = this.http.get(accountUrl, this.httpOptions())
      .toPromise()

    accountPromise
      .then(response => {
        this.accountInformation = response;
      })
    return accountPromise;
  }

  public listDeviceInformation(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/device/list`;
    let devicePromise = this.http.get(accountUrl, this.httpOptions())
      .toPromise()

    devicePromise
      .then(response => {
        this.accountInformation = response;
      })
    return devicePromise;
  }

  public getFiringStatistics(): Promise<any>  {
    var kilnUrl = this.local_uri + "/admin/info/statistics";
    let headers = this.httpOptions()
    return this.http.get(kilnUrl, headers).toPromise()
  }

  public listPremiumUserInformation(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/users/list`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise()
  }

  public premiumUserStatistics(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/users/premium/statistics`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise()
  }

  public userCount(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/users/count`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise()
  }

  public userEvents(): Promise<any>{
    var accountUrl = `${this.local_uri}/admin/users/events`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise()
  }

  public localCacheAccountInfo() {
    return this.accountInformation;
  }

  public getDevicesForAccount(): Promise<any>{
    var accountUrl = `${this.local_uri}/account/${this.getCurrentAccount()}/devices`;
    return this.http.get(accountUrl, this.httpOptions())
      .toPromise()
  }

  public getDevicesForAccountId(accountId: number): Promise<{ kilns?: any[]; network_devices?: any[] }> {
    const accountUrl = `${this.local_uri}/account/${accountId}/devices`;
    return this.http.get<{ kilns?: any[]; network_devices?: any[] }>(accountUrl, this.httpOptions()).toPromise();
  }

  public lookupKilnSerialForAccount(
    serial: string,
    account_id?: number
  ): Promise<{ canName: boolean }> {
    const accountId = account_id ?? this.getCurrentAccount();
    const accountUrl = `${this.local_uri}/account/${accountId}/devices/lookup/${encodeURIComponent(serial)}`;
    console.warn('[AccountService lookupKilnSerial] request', { serial, accountId, accountUrl });
    const startedAt = Date.now();
    return this.http.get<{ canName: boolean }>(
      accountUrl,
      this.httpOptions()
    ).toPromise()
      .then((result) => {
        console.warn('[AccountService lookupKilnSerial] success', {
          serial,
          accountId,
          elapsedMs: Date.now() - startedAt,
          result,
        });
        return result;
      })
      .catch((err) => {
        console.warn('[AccountService lookupKilnSerial] failed', {
          serial,
          accountId,
          elapsedMs: Date.now() - startedAt,
          httpStatus: err?.status ?? err?.statusCode,
          errMessage: err?.message,
          errorBody: err?.error,
          err,
        });
        throw err;
      });
  }

  public upsertNetworkDeviceForAccount(serial_number, data, account_id?): Promise<any>{
    var accountUrl = `${this.local_uri}/account/${account_id || this.getCurrentAccount()}/devices/${serial_number}`;
    return this.http.post(accountUrl, data, this.httpOptions())
      .toPromise()
  }

  public upsertKilnForAccount(serial_number, data): Promise<any>{
    let is_upsert = (data.external_id || serial_number)
    var accountUrl = is_upsert ? `${this.local_uri}/account/${this.getCurrentAccount()}/devices/${is_upsert}` : `${this.local_uri}/account/${this.getCurrentAccount()}/devices`;
    if(data.external_id) {
      delete data.external_id;
    }

    Object.keys(data).forEach((key) => {
      if (data[key] === '') {
        delete data[key];
      }
    });

    if(is_upsert) {
      return this.http.put(accountUrl, data, this.httpOptions())
        .toPromise()
        // .then(response => { this.events.publish('upsertAccountKiln:success', response) })
        // .catch(error => {this.events.publish('upsertAccountKiln:error', error)});
    } else {
      return this.http.post(accountUrl, data, this.httpOptions())
        .toPromise()
        // .then(response => { this.events.publish('upsertAccountKiln:success', response) })
        // .catch(error => {this.events.publish('upsertAccountKiln:error', error)});
    }
  }

  public getUsersForAccount(): Promise<any>{
    var accountUrl = `${this.local_uri}/account/${this.getCurrentAccount()}/users`;
    return this.http.get(accountUrl, this.httpOptions())
      .toPromise()
  }

  public updateGeneral(
    account_id,
    timezone,
    notificationSettings?: {
      sms_notifications_enabled?: boolean;
      email_alerts_enabled?: boolean;
      push_notifications_enabled?: boolean;
      weekly_summary?: boolean;
    }
  ): Promise<any> {
    const company: Record<string, unknown> = {
      timezone: timezone,
    };
    if (notificationSettings) {
      if (notificationSettings.sms_notifications_enabled != null) {
        company.sms_notifications_enabled = notificationSettings.sms_notifications_enabled;
      }
      if (notificationSettings.email_alerts_enabled != null) {
        company.email_alerts_enabled = notificationSettings.email_alerts_enabled;
      }
      if (notificationSettings.push_notifications_enabled != null) {
        company.push_notifications_enabled = notificationSettings.push_notifications_enabled;
      }
      if (notificationSettings.weekly_summary != null) {
        company.weekly_summary = notificationSettings.weekly_summary;
      }
    }
    let body = { company };

    return this.update(account_id, body, 'General');
  }

  public updateCompanyInfo(account_id, name, weekly_summary, address, owner_name): Promise<any>{
    let body = {
      company: {
        name: name,
        weekly_summary: weekly_summary,
        address: address
      },
      account_owner: {
        owner: {first: owner_name.first_name, last: owner_name.last_name}
      }
    }

    return this.update(account_id, body, 'Company');
  }

  public updateOwnerInfo(account_id, address, owner_name): Promise<any>{
    let body = {
      account_owner: {
        owner: {first: owner_name.first_name, last: owner_name.last_name},
        address: address
      }
    };

    return this.update(account_id, body, 'Owner');
  }

  update(id, body, component): Promise<any>{
    var accountUrl = `${this.local_uri}/account/${id}`;
    return this.http.put(accountUrl, body, this.httpOptions()).toPromise()
  }

  public upgradeAccount(account_id): Promise<any> {
    var accountUrl = `${this.local_uri}/account/${account_id}/upgrade`;
    return this.http.post(accountUrl, {}, this.httpOptions()).toPromise();
  }

  create(body): Promise<any> {
    var accountUrl = `${this.local_uri}/account`;
    return this.http.post(accountUrl, body, this.httpOptions())
      .toPromise()
  }

  createByOwner(body): Promise<any> {
    return this.create(body)    
  }

  createFreeAccount(name: string): Promise<{ id: number; name: string }> {
    const accountUrl = `${this.local_uri}/accounts/free`;
    return this.http.post<{ id: number; name: string }>(accountUrl, { name }, this.httpOptions()).toPromise();
  }

  upgrade(account_id, body): Promise<any> {
    var accountUrl = `${this.local_uri}/account/${account_id}/upgrade`;
    return this.http.post(accountUrl, body, this.httpOptions())
      .toPromise()
  }

  createNetworkDevice(account_id, body): Promise<any> { //should return promise
    var accountUrl = `${this.local_uri}/account/${account_id}/devices`;
    return this.http.post(accountUrl, body, this.httpOptions())
      .toPromise()
  }

  unlinkNetworkDevice(account_id, body): Promise<any> { //should return promise
    var accountUrl = `${this.local_uri}/account/${account_id}/devices/unlink`;
    return this.http.put(accountUrl, body, this.httpOptions())
      .toPromise()
  }

  delete(account, permanent = false): Promise<any> { //should return promise
    var accountUrl = `${this.local_uri}/account/${account.id}`;
    if(permanent) { accountUrl += '?permanent=true'; }
    return this.http.delete(accountUrl, this.httpOptions()).toPromise()
  }

  public getAccountStorageUsage(accountId: string): Promise<any> {
    console.log(`getAccountStorageUsage called with accountId: ${accountId}`);
    const accountUrl = `${this.local_uri}/account/${accountId}/storage`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise();
  }

  public getAccountStorageAnalysis(accountId: string): Promise<any> {
    const accountUrl = `${this.local_uri}/account/${accountId}/storage/analysis`;
    return this.http.get(accountUrl, this.httpOptions()).toPromise();
  }

  public getAdminStorageData(): Promise<AdminStorageData> {
    const url = `${this.local_uri}/admin/storage`;
    return this.http.get<AdminStorageData>(url, this.httpOptions()).toPromise();
  }

  /** Terms and conditions (Skutt admin / super-admin). */
  public getTermsAndConditions(): Promise<TermsAndConditionsRow[]> {
    const url = `${this.local_uri}/admin/terms-and-conditions`;
    return this.http.get<TermsAndConditionsRow[]>(url, this.httpOptions()).toPromise();
  }

  public createTermsAndConditions(version: number, endpoint?: string | null, content?: string | null): Promise<TermsAndConditionsRow> {
    const url = `${this.local_uri}/admin/terms-and-conditions`;
    return this.http.post<TermsAndConditionsRow>(url, { version, endpoint: endpoint || undefined, content: content || undefined }, this.httpOptions()).toPromise();
  }

  /** Login auth failures (super-admin, tech, skutt-admin). */
  public listLoginAuthFailures(params?: { email?: string; limit?: number }): Promise<LoginAuthFailureRow[]> {
    let httpParams = new HttpParams();
    if (params?.email) {
      httpParams = httpParams.set('email', params.email);
    }
    if (params?.limit != null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }
    const url = `${this.local_uri}/admin/login-failures`;
    return this.http
      .get<LoginAuthFailureRow[]>(url, { ...this.httpOptions(), params: httpParams })
      .toPromise();
  }

  public getLoginAuthFailure(username: string): Promise<LoginAuthFailureRow> {
    const url = `${this.local_uri}/admin/login-failures/${encodeURIComponent(username)}`;
    return this.http.get<LoginAuthFailureRow>(url, this.httpOptions()).toPromise();
  }

  /** Kiln claim failures (super-admin, tech, skutt-admin). */
  public listKilnClaimFailures(params?: { email?: string; serial?: string; limit?: number }): Promise<KilnClaimFailureRow[]> {
    let httpParams = new HttpParams();
    if (params?.email) {
      httpParams = httpParams.set('email', params.email);
    }
    if (params?.serial) {
      httpParams = httpParams.set('serial', params.serial);
    }
    if (params?.limit != null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }
    const url = `${this.local_uri}/admin/kiln-claim-failures`;
    return this.http
      .get<KilnClaimFailureRow[]>(url, { ...this.httpOptions(), params: httpParams })
      .toPromise();
  }

  public getKilnClaimFailure(email: string, serial: string): Promise<KilnClaimFailureRow> {
    let httpParams = new HttpParams().set('email', email).set('serial', serial);
    const url = `${this.local_uri}/admin/kiln-claim-failures/detail`;
    return this.http.get<KilnClaimFailureRow>(url, { ...this.httpOptions(), params: httpParams }).toPromise();
  }

  public adminListDistributors(): Promise<DistributorApiRow[]> {
    const url = `${this.local_uri}/admin/distributors`;
    return this.http.get<DistributorApiRow[]>(url, this.httpOptions()).toPromise();
  }

  public adminCreateDistributor(body: {
    name: string;
    address: DistributorApiRow['address'];
    user_ids?: number[];
  }): Promise<DistributorApiRow> {
    const url = `${this.local_uri}/admin/distributors`;
    return this.http.post<DistributorApiRow>(url, body, this.httpOptions()).toPromise();
  }

  public adminUpdateDistributor(
    id: number,
    body: Partial<{ name: string; address: DistributorApiRow['address']; user_ids: number[] }>
  ): Promise<DistributorApiRow> {
    const url = `${this.local_uri}/admin/distributors/${id}`;
    return this.http.put<DistributorApiRow>(url, body, this.httpOptions()).toPromise();
  }

  public adminDeleteDistributor(id: number): Promise<void> {
    const url = `${this.local_uri}/admin/distributors/${id}`;
    return this.http.delete<void>(url, this.httpOptions()).toPromise();
  }

  public listDistributorsForAccount(accountId: number): Promise<DistributorApiRow[]> {
    const url = `${this.local_uri}/account/${accountId}/distributors`;
    return this.http.get<DistributorApiRow[]>(url, this.httpOptions()).toPromise();
  }

  public setAccountDistributor(accountId: number, distributorId: number | null): Promise<{ success: boolean; distributor_id: number | null }> {
    const url = `${this.local_uri}/account/${accountId}/distributor`;
    return this.http
      .put<{ success: boolean; distributor_id: number | null }>(url, { distributor_id: distributorId }, this.httpOptions())
      .toPromise();
  }

  httpOptions() {
    if(this.userService && this.userService.getLoginData()) {
      return {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-access-token': this.userService.getLoginData().token
        })
      }
    } else {
      return {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        })
      }
    }

  }
}
